import { useState } from 'react';
import axios from 'axios';

// Create or edit a user: details, role, which tenant sites they can use,
// and which accounts (and so meters) they can see.

const inputStyle = {
  width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)',
  border: '1.5px solid rgba(56,189,248,0.25)', borderRadius: 10, padding: '10px 14px',
  color: '#e2e8f0', fontSize: 15, outline: 'none', fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', color: '#e2e8f0', fontSize: 12.5, letterSpacing: 1.2,
  textTransform: 'uppercase', marginBottom: 6, fontWeight: 600,
};

function CheckList({ title, items, selected, onChange, render, empty, disabled }) {
  const [q, setQ] = useState('');
  const shown = items.filter((it) => render(it).toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const allShownSelected = shown.length > 0 && shown.every((it) => selected.includes(it.id));

  return (
    <div style={{ marginBottom: 18, opacity: disabled ? 0.5 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>{title} ({selected.length} selected)</label>
        {shown.length > 0 && !disabled && (
          <button type="button"
            onClick={() => onChange(allShownSelected
              ? selected.filter((id) => !shown.some((it) => it.id === id))
              : [...new Set([...selected, ...shown.map((it) => it.id)])])}
            style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: 13.5, cursor: 'pointer' }}>
            {allShownSelected ? 'Clear' : 'Select all'}
          </button>
        )}
      </div>
      {items.length > 6 && (
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ ...inputStyle, padding: '8px 12px', marginBottom: 6 }} disabled={disabled} />
      )}
      <div style={{ maxHeight: 170, overflowY: 'auto', border: '1px solid rgba(56,189,248,0.15)', borderRadius: 10, padding: 6 }}>
        {items.length === 0 && <div style={{ color: '#cbd5e1', fontSize: 14.5, padding: 8 }}>{empty}</div>}
        {shown.map((it) => (
          <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6, cursor: disabled ? 'default' : 'pointer', color: '#e2e8f0', fontSize: 14.5 }}>
            <input type="checkbox" checked={selected.includes(it.id)} onChange={() => toggle(it.id)} disabled={disabled} />
            {render(it)}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function UserModal({ user, tenants, accounts, headers, currentUserId, onClose, onSaved }) {
  const isNew = !user;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'user',
    tenant_ids: (user?.tenants || []).map((t) => t.id),
    account_ids: (user?.account_ids || []).map(Number),
    send_invite: true, // new users: email them a link to set their own password
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isAdmin = form.role === 'admin';

  const save = async () => {
    setError('');
    const inviting = isNew && form.send_invite;
    if (!form.name || !form.email || (isNew && !inviting && !form.password)) {
      return setError('Name, email' + (isNew && !inviting ? ' and password' : '') + ' are required');
    }
    if (form.password && form.password.length < 8) return setError('Password must be at least 8 characters');

    const body = { ...form };
    if (!body.password || inviting) delete body.password;
    if (!isNew) delete body.send_invite;
    setSaving(true);
    try {
      let notice = null;
      if (isNew) {
        const r = await axios.post('/api/users', body, { headers });
        if (r.data.invite?.sent) notice = { ok: true, text: `${form.name} was added and an invitation was emailed to ${form.email}.` };
        else if (r.data.invite && !r.data.invite.sent) notice = { ok: false, text: `${form.name} was added, but the invitation email failed (${r.data.invite.error}). Use “Resend invite” once email works.` };
        else notice = { ok: true, text: `${form.name} was added.` };
      } else {
        await axios.put(`/api/users/${user.id}`, body, { headers });
      }
      onSaved(notice);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mw-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
      <div className="mw-modal" style={{ background: '#0f172a', borderRadius: 16, border: '1px solid rgba(56,189,248,0.2)', padding: 28, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto' }}>
        <h3 style={{ color: '#ffffff', fontSize: 20, fontWeight: 700, margin: '0 0 20px' }}>
          {isNew ? 'Create New User' : `Edit User — ${user.name}`}
        </h3>

        {error && (
          <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: '10px 14px', color: '#f87171', fontSize: 14.5, marginBottom: 16 }}>{error}</div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Full Name</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. John Doe" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="john@example.com" style={inputStyle} />
          </div>
          {isNew && (
            <label style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(74,155,224,0.12)', border: '1px solid rgba(74,155,224,0.4)', borderRadius: 10, padding: '10px 12px', color: '#ffffff', fontSize: 14.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm((f) => ({ ...f, send_invite: e.target.checked }))} style={{ width: 18, height: 18, marginTop: 2 }} />
              <span>Email an invitation so they choose their own password
                <span style={{ display: 'block', color: '#cbd5e1', fontSize: 13 }}>The link works once and expires after 3 days. Untick to set a password yourself.</span>
              </span>
            </label>
          )}
          {!(isNew && form.send_invite) && (
            <div>
              <label style={labelStyle}>{isNew ? 'Password' : 'New password'}</label>
              <input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={isNew ? 'Min 8 characters' : 'Leave blank to keep current'} style={inputStyle} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Role</label>
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              disabled={user?.id === currentUserId} style={inputStyle}>
              <option value="user">Regular User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>

        {isAdmin && (
          <div style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)', borderRadius: 10, padding: '10px 14px', color: '#7dd3fc', fontSize: 14.5, marginBottom: 16 }}>
            Admins can sign in on every tenant site and see all accounts. The selections below only matter if the role changes back to Regular User.
          </div>
        )}

        <CheckList
          title="Tenant sites"
          items={tenants}
          selected={form.tenant_ids}
          onChange={(ids) => setForm((f) => ({ ...f, tenant_ids: ids }))}
          render={(t) => `${t.name} — ${t.subdomain}.smartwater.co.ke${t.active === false ? ' (inactive)' : ''}`}
          empty="No tenants yet"
          disabled={isAdmin}
        />

        <CheckList
          title="Accounts"
          items={accounts}
          selected={form.account_ids}
          onChange={(ids) => setForm((f) => ({ ...f, account_ids: ids }))}
          render={(a) => `${a.name}${a.meter_count !== undefined ? ` · ${a.meter_count} meters` : ''}`}
          empty="No accounts yet"
          disabled={isAdmin}
        />

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button onClick={onClose} style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 12, color: '#e2e8f0', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ flex: 1, background: 'linear-gradient(135deg, #0891b2, #0e7490)', border: 'none', borderRadius: 10, padding: 12, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Saving…' : isNew ? 'Create User' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
